import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import {
  type DashboardAgentModeBudget,
  listAgentModeBudgetsAsUser,
  upsertAgentModeBudgetsAsUser,
} from "../../dashboard-client";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { EmptyState } from "../common/EmptyState";
import { FormInput } from "../common/forms/FormInput";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";

type BudgetDraftMap = Record<string, string>;

const buildKey = (mode: string, phase: string) => `${mode}::${phase}`;

const BUDGET_HEADER_LABELS = ["Mode", "Phase", "Current", "New limit"] as const;

const formatTokens = (value: number | null): string => {
  if (value == null) {
    return "—";
  }
  return value.toLocaleString();
};

const BudgetTableHeader: FC = () => (
  <DataTable.Header>
    <DataTable.Row>
      {BUDGET_HEADER_LABELS.map((label) => (
        <DataTable.HeaderCell
          key={label}
          className={label === "Current" ? "text-right" : undefined}
        >
          {label}
        </DataTable.HeaderCell>
      ))}
      <DataTable.HeaderCell className="w-px" />
    </DataTable.Row>
  </DataTable.Header>
);

const BudgetPhaseRow: FC<{
  draftValue: string;
  isDirty: boolean;
  isSaved: boolean;
  isSaving: boolean;
  mode: string;
  onChange: (value: string) => void;
  onSave: () => void;
  phase: DashboardAgentModeBudget;
  showModeBadge: boolean;
}> = ({
  draftValue,
  isDirty,
  isSaved,
  isSaving,
  mode,
  onChange,
  onSave,
  phase,
  showModeBadge,
}) => {
  const RowEl = showModeBadge ? DataTable.Row : DataTable.SubRow;

  return (
    <RowEl>
      <DataTable.Cell className="align-middle">
        {showModeBadge ? (
          <Badge variant="info" size="sm">
            {mode}
          </Badge>
        ) : null}
      </DataTable.Cell>
      <DataTable.Cell className="align-middle">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
          {phase.phase}
        </span>
      </DataTable.Cell>
      <DataTable.Cell className="text-right align-middle">
        <span className="font-mono text-xs tabular-nums text-gray-400 dark:text-white/35">
          {formatTokens(phase.max_input_token_budget)}
        </span>
      </DataTable.Cell>
      <DataTable.Cell className="align-middle">
        <FormInput
          type="number"
          min="0"
          placeholder="—"
          size="sm"
          value={draftValue}
          onChange={(e) => onChange(e.target.value)}
          className="min-w-[100px] max-w-[140px] tabular-nums"
        />
      </DataTable.Cell>
      <DataTable.Cell className="align-middle">
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            disabled={isSaving || !isDirty}
            onClick={onSave}
            variant="primary"
            size="sm"
            className="disabled:opacity-30"
          >
            {isSaving ? "..." : "Save"}
          </Button>
          {isSaved ? (
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">
              Saved
            </span>
          ) : null}
        </div>
      </DataTable.Cell>
    </RowEl>
  );
};

const AgentModeBudgetsPanel: FC = () => {
  const [budgets, setBudgets] = useState<DashboardAgentModeBudget[]>([]);
  const [drafts, setDrafts] = useState<BudgetDraftMap>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const budgetsResult = await listAgentModeBudgetsAsUser();
        const rows = budgetsResult.data.budgets;
        if (!isMounted) {
          return;
        }
        setBudgets(rows);
        setDrafts(buildDrafts(rows));
        setError(null);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }
        const message =
          loadError instanceof Error ? loadError.message : String(loadError);
        setError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      isMounted = false;
    };
  }, []);

  const grouped = useMemo(() => groupBudgets(budgets), [budgets]);

  const handleChange = useCallback((key: string, value: string) => {
    setDrafts((current) => ({ ...current, [key]: value }));
    setSavedKey((current) => (current === key ? null : current));
  }, []);

  const handleSave = useCallback(
    async (mode: string, phase: string) => {
      const key = buildKey(mode, phase);
      const rawValue = drafts[key] ?? "";
      const parsed = parseBudgetValue(rawValue);
      if (parsed.invalid) {
        setError(
          parsed.message ?? "Budget must be a positive number or left blank.",
        );
        return;
      }

      try {
        setSavingKey(key);
        setError(null);
        const upsertResult = await upsertAgentModeBudgetsAsUser({
          body: { max_input_token_budget: parsed.value, mode, phase },
        });
        const updated = upsertResult.data.budgets;
        setBudgets((current) => mergeBudgets(current, updated));
        setSavedKey(key);
      } catch (saveError) {
        const message =
          saveError instanceof Error ? saveError.message : String(saveError);
        setError(message);
      } finally {
        setSavingKey((current) => (current === key ? null : current));
      }
    },
    [drafts],
  );

  if (loading) {
    return (
      <PanelSection title="Agent Phase Budgets">
        <LoadingState variant="skeleton" rows={6} rowHeight="h-8" />
      </PanelSection>
    );
  }

  return (
    <div className="space-y-4">
      {error && <Alert variant="warning">{error}</Alert>}

      <PanelSection
        title="Agent Phase Budgets"
        badge={<Badge size="sm">{String(budgets.length)}</Badge>}
      >
        {grouped.length === 0 ? (
          <EmptyState
            title="No budgets configured"
            description="Ensure the orchestrator has seeded default agent mode budgets."
            size="sm"
          />
        ) : (
          <DataTable responsive>
            <BudgetTableHeader />
            {grouped.map(({ mode, phases }) => (
              <DataTable.RowGroup key={mode}>
                {phases.map((phase, phaseIdx) => {
                  const key = buildKey(mode, phase.phase);
                  const isSaving = savingKey === key;
                  const isSaved = savedKey === key;
                  const isDirty =
                    (drafts[key] ?? "") !==
                    (phase.max_input_token_budget == null
                      ? ""
                      : String(phase.max_input_token_budget));
                  return (
                    <BudgetPhaseRow
                      key={key}
                      draftValue={drafts[key] ?? ""}
                      isDirty={isDirty}
                      isSaved={isSaved}
                      isSaving={isSaving}
                      mode={mode}
                      onChange={(value) => handleChange(key, value)}
                      onSave={() => void handleSave(mode, phase.phase)}
                      phase={phase}
                      showModeBadge={phaseIdx === 0}
                    />
                  );
                })}
              </DataTable.RowGroup>
            ))}
          </DataTable>
        )}
      </PanelSection>
    </div>
  );
};

function groupBudgets(budgets: DashboardAgentModeBudget[]) {
  const grouped = new Map<string, DashboardAgentModeBudget[]>();
  budgets.forEach((entry) => {
    const list = grouped.get(entry.mode) ?? [];
    list.push(entry);
    grouped.set(entry.mode, list);
  });
  return Array.from(grouped.entries())
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([mode, phases]) => ({
      mode,
      phases: phases.slice().toSorted((a, b) => a.phase.localeCompare(b.phase)),
    }));
}

function buildDrafts(budgets: DashboardAgentModeBudget[]): BudgetDraftMap {
  return budgets.reduce<BudgetDraftMap>((acc, entry) => {
    acc[buildKey(entry.mode, entry.phase)] =
      entry.max_input_token_budget == null
        ? ""
        : String(entry.max_input_token_budget);
    return acc;
  }, {});
}

function parseBudgetValue(value: string): {
  invalid: boolean;
  message?: string;
  value: number | null;
} {
  const trimmed = value.trim();
  if (!trimmed) {
    return { invalid: false, value: null };
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return {
      invalid: true,
      message: "Budget must be a positive number or left blank.",
      value: null,
    };
  }
  return { invalid: false, value: Math.trunc(parsed) };
}

function mergeBudgets(
  current: DashboardAgentModeBudget[],
  updated: DashboardAgentModeBudget[],
): DashboardAgentModeBudget[] {
  if (updated.length === 0) {
    return current;
  }
  const updates = new Map(
    updated.map((entry) => [buildKey(entry.mode, entry.phase), entry]),
  );
  const merged = current.map((entry) => {
    const replacement = updates.get(buildKey(entry.mode, entry.phase));
    return replacement ?? entry;
  });
  const mergedKeys = new Set(
    merged.map((entry) => buildKey(entry.mode, entry.phase)),
  );
  for (const entry of updated) {
    const key = buildKey(entry.mode, entry.phase);
    if (mergedKeys.has(key)) {
      continue;
    }
    merged.push(entry);
    mergedKeys.add(key);
  }
  return merged;
}

export default AgentModeBudgetsPanel;
